/**
 * 模拟核心的数值常量（可被关卡覆盖）
 *
 * 纪律：这里的数字是整个游戏唯一允许出现的"魔法数字"，
 * 关卡通过 `MatchSetup.constants` 覆盖其中任意项。
 * 改这里的默认值等于改全局难度 —— 必须同步跑 `npm test` 并注明理由。
 */

import type { SimConstants } from "./types.js";

/** 默认数值：等价于骨架版本的硬编码常量，保证既有行为不漂移 */
export const DEFAULT_CONSTANTS: SimConstants = {
  litThreshold: 50,
  energyPerTurn: 10,
  spreadBase: 12,
  answerWisdom: 15,
  answerHalfWisdom: 5,
  turnLimit: 40,
  startEnergy: 30,
  energyCap: 30,
  startBonus: 15,
  spreadWisdom: 3,
};

/** 每回合触发随机事件的默认概率 */
export const DEFAULT_EVENT_CHANCE = 0.35;

/** 把关卡给的局部覆盖合并到默认值上 */
export function resolveConstants(partial?: Partial<SimConstants>): SimConstants {
  return { ...DEFAULT_CONSTANTS, ...partial };
}

/** 迷雾浓度上限（同时是"照亮大地"的镜像进度条） */
export const MIST_MAX = 100;

/** 启蒙度上限 */
export const ENLIGHTENMENT_MAX = 100;

/** 在一块新区域开辟火种营地的能量消耗（所有关卡一致，不开放覆盖） */
export const IGNITE_COST = 10;

/** 已点亮区域每回合的自燃增量 */
export const SELF_GROWTH_PER_TURN = 2;

/**
 * 错题间隔复习的天数台阶（GDD 3.3）：1 / 3 / 7 / 15 天。
 * 与知识卡 schema 里 `reviewStages` 的默认值保持一致 —— 客户端本地复习、
 * 服务端错题表、内容卡三者用的是同一串数字。
 */
export const REVIEW_STAGE_DAYS = [1, 3, 7, 15] as const;
export type ReviewStageDays = typeof REVIEW_STAGE_DAYS;

/**
 * 迷雾压制的浓度台阶：迷雾每满一个台阶，**未点亮**区域每回合就会被侵蚀 1 点启蒙度。
 * 例：迷雾 50 → 未点亮区域每回合 −2。这是"迷雾随时间增强、逼玩家应对"的
 * 唯一硬约束（GDD 2.2「迷雾侵蚀」），也是迷雾抗性类技能节点的价值来源。
 * 已点亮区域不受压制（只受"谣言四起"等事件影响），形成"先稳固、再扩张"的节奏。
 */
export const MIST_SUPPRESS_STEP = 25;
